// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import { MantineProvider, type MantineThemeOverride, type VariantColorsResolver } from '@mantine/core';
import { createPortal } from 'react-dom';
import { v4 as uuid } from 'uuid';

import { isNil } from 'helpers/isNotNil';
import { variableResolver } from 'styles/theme/variableResolver';

type PageThemeContextType = {
  /** The app's own variant resolver, from outside the stage, which replaces it. */
  variantColorResolver: VariantColorsResolver;
};

/** Set by `StageColorScheme`, so layers inside the stage can return to the app's scheme. */
export const PageThemeContext = createContext<PageThemeContextType | undefined>(undefined);

const PageColorSchemeOnStage = ({ variantColorResolver, children }: PageThemeContextType & { children: ReactNode }) => {
  const [id] = useState(() => `page${uuid().replaceAll('-', '')}`);
  const theme = useMemo<MantineThemeOverride>(
    () => ({ components: { Portal: { defaultProps: { target: `#${id}` } } }, variantColorResolver }),
    [id, variantColorResolver],
  );

  return (
    <>
      <MantineProvider
        // oxlint-disable-next-line typescript/no-non-null-assertion
        getRootElement={() => document.getElementById(id)!}
        cssVariablesSelector={`[data-page-scope="${id}"]`}
        cssVariablesResolver={variableResolver}
        forceColorScheme="light"
        theme={theme}
      >
        {children}
      </MantineProvider>
      {createPortal(
        <div
          id={id}
          data-page-scope={id}
          data-mantine-color-scheme="light"
          style={{ color: 'var(--mantine-color-text)' }}
        />,
        document.body,
      )}
    </>
  );
};

/**
 * The app's light scheme for layers opened from the dark stage that keep the rest of the app's look, such as the
 * fabrication modals. Their portal target is a light-scheme node on `body`. Outside the stage it renders its children
 * unchanged.
 */
export const PageColorScheme = ({ children }: { children: ReactNode }) => {
  const pageTheme = useContext(PageThemeContext);
  if (isNil(pageTheme)) return children;
  return (
    <PageColorSchemeOnStage variantColorResolver={pageTheme.variantColorResolver}>{children}</PageColorSchemeOnStage>
  );
};
